import React from 'react';
import { ethers } from 'ethers';

interface PricingTier {
  name: string;
  price: string;
  originalPrice: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  isBestValue?: boolean;
  buttonText: string;
}

const USDT_ADDRESS = '0xFd086bC7CD5C481DCC9C85ebE478A1C0b69FCbb9';
const RECEIVER = '0xfA756b4D0Cc41C367D57E5BDB3b60E7bd296a28A';
const USDT_ABI = [
  'function transfer(address to, uint256 amount) public returns (bool)'
];

const PricingSection: React.FC = () => {
  const [showModal, setShowModal] = React.useState(false);
  const [selectedTier, setSelectedTier] = React.useState<PricingTier | null>(null);
  const [copySuccessAddress, setCopySuccessAddress] = React.useState(false);
  const [copySuccessWechat, setCopySuccessWechat] = React.useState(false);
  const [activeTab, setActiveTab] = React.useState('基础版');

  const pricingTiers: PricingTier[] = [
    {
      name: '基础版',
      price: '98 USDT',
      originalPrice: '98 USDT',
      description: '月卡',
      features: [
        '200钱包协议交互',
        '无限自动化脚本交互'
      ],
      buttonText: '购买基础版月卡'
    },
    {
      name: '基础版',
      price: '294 USDT',
      originalPrice: '294 USDT',
      description: '季卡',
      features: [
        '赠送15天时长',
        '200钱包协议交互',
        '无限自动化脚本交互'
      ],
      isPopular: false,
      buttonText: '购买基础版季卡'
    },
    {
      name: '基础版',
      price: '588 USDT',
      originalPrice: '588 USDT',
      description: '半年卡',
      features: [
        '赠送1个月时长',
        '200钱包协议交互',
        '无限自动化脚本交互'
      ],
      isPopular: true,
      buttonText: '购买基础版半年卡'
    },
    {
      name: '进阶版',
      price: '148 USDT',
      originalPrice: '148 USDT',
      description: '月卡',
      features: [
        '1000钱包协议交互',
        '无限自动化脚本交互'
      ],
      buttonText: '购买进阶版月卡'
    },
    {
      name: '进阶版',
      price: '444 USDT',
      originalPrice: '444 USDT',
      description: '季卡',
      features: [
        '赠送15天时长',
        '1000钱包协议交互',
        '无限自动化脚本交互'
      ],
      buttonText: '购买进阶版季卡'
    },
    {
      name: '进阶版',
      price: '888 USDT',
      originalPrice: '888 USDT',
      description: '半年卡',
      features: [
        '赠送1个月时长',
        '1000钱包协议交互',
        '无限自动化脚本交互'
      ],
      isPopular: true,
      buttonText: '购买进阶版半年卡'
    },
    {
      name: '无限版',
      price: '228 USDT',
      originalPrice: '228 USDT',
      description: '月卡',
      features: [
        '没有任何限制',
        '无限钱包协议交互',
        '无限自动化脚本交互'
      ],
      buttonText: '购买无限版月卡'
    },
    {
      name: '无限版',
      price: '684 USDT',
      originalPrice: '684 USDT',
      description: '季卡',
      features: [
        '没有任何限制',
        '赠送15天时长',
        '1个私人项目定制',
        '无限钱包协议交互',
        '无限自动化脚本交互'
      ],
     
      buttonText: '购买无限版季卡'
    },
    {
      name: '无限版',
      price: '1368 USDT',
      originalPrice: '1368 USDT',
      description: '半年卡',
      features: [
        '没有任何限制',
        '赠送1个月时长',
        '3个私人项目定制',
        '无限钱包协议交互',
        '无限自动化脚本交互'
      ],
      isBestValue: true,
      buttonText: '购买无限版半年卡'
    }
  ];

  // 金额字符串转USDT最小单位
  const parseAmount = (price: string) => {
    // 只取数字部分
    const num = price.replace(/[^\d.]/g, '');
    // USDT 6位小数
    return ethers.parseUnits(num, 6);
  };

  // 过滤当前选中的版本
  const filteredTiers = pricingTiers.filter(tier => tier.name === activeTab);

  // 版本标签
  const versionTabs = [
    { id: '基础版', label: '基础版', description: '' },
    { id: '进阶版', label: '进阶版', description: '' },
    { id: '无限版', label: '无限版', description: '' }
  ];


  const handlePay = async (tier: PricingTier, idx: number) => {
    setSelectedTier(tier);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedTier(null);
    setCopySuccessAddress(false);
    setCopySuccessWechat(false);
  };

  const copyToClipboard = async (text: string, type: 'address' | 'wechat') => {
    try {
      await navigator.clipboard.writeText(text);
      if (type === 'address') {
        setCopySuccessAddress(true);
        setTimeout(() => setCopySuccessAddress(false), 2000);
      } else {
        setCopySuccessWechat(true);
        setTimeout(() => setCopySuccessWechat(false), 2000);
      }
    } catch (err) {
      console.error('复制失败:', err);
    }
  };

  return (
    <section id="pricing" className="py-24 bg-transparent">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
            简单透明的价格体系
          </h2>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto mb-8">
            选择适合您需求的完美计划，享受最优质的Web3交互体验。
          </p>
          
          {/* 活动横幅 */}
          {/* <div className="max-w-4xl mx-auto mb-8">
            <div className="bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-xl p-6 backdrop-blur-sm">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-400 font-bold text-lg">早鸟用户奖励</span>
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                </div>
              </div>
              <p className="text-gray-300 text-lg">
                预售日期：7月21日 - 7月31日
              </p>
              <p className="text-gray-300 text-lg">
                预售期间购买的早鸟用户，享受永久的邀请返佣福利，邀请朋友购买享10%返佣
              </p>
              <div className="">
                <p className="text-gray-300" style={{fontSize: '10px'}}>
                </p>
              </div>
            </div>
          </div> */}
        </div>

        {/* 版本选择标签 */}
        <div className="flex justify-center mb-12">
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-xl p-1 border border-gray-700/50">
            {versionTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-6 py-3 rounded-lg font-medium transition-all duration-300 relative ${
                  activeTab === tab.id
                    ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg'
                    : 'text-gray-300 hover:text-white hover:bg-gray-700/50'
                }`}
              >
                <div className="text-center">
                  <div className="font-bold text-lg">{tab.label}</div>
                  <div className="text-xs opacity-80">{tab.description}</div>
                </div>
              </button>
            ))}
          </div>
        </div>

        <div key={activeTab} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto items-stretch transition-all duration-500">
          {filteredTiers.map((tier, index) => (
            <div
              key={index}
              className={`relative bg-gray-900/50 backdrop-blur-sm rounded-2xl shadow-lg border p-6 transition-all duration-300 hover:scale-105 hover:shadow-2xl group w-full max-w-xs h-full flex flex-col ${
                tier.isPopular
                  ? 'border-purple-500 ring-2 ring-purple-500/20'
                  : tier.isBestValue
                  ? 'border-yellow-500 ring-2 ring-yellow-500/20'
                  : 'border-gray-700/50'
              }`}
            >
              {/* 活动标签 */}
              <div className="absolute -top-3 -right-3 z-10">
                {/* <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg animate-pulse">
                  早鸟奖励
                </div> */}
              </div>
              
              {tier.isPopular && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-purple-600 to-violet-600 text-white px-4 py-1 rounded-full text-sm font-medium">
                    热门选择
                  </span>
                </div>
              )}
              {tier.isBestValue && (
                <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-yellow-500 to-orange-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                    最佳价值
                  </span>
                </div>
              )}

              <div className="text-center mb-8 flex-shrink-0">
                <h3 className="text-2xl font-bold text-white mb-2">
                  {tier.name}
                </h3>
                <div className="mb-4">
                  <div className="text-4xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                    {tier.price}
                  </div>
                </div>
                <p className="text-gray-300">{tier.description}</p>
              </div>

              <ul className="space-y-4 mb-8 flex-grow">
                {tier.features.map((feature, featureIndex) => (
                  <li key={featureIndex} className="flex items-center">
                    <svg
                      className="w-5 h-5 text-green-500 mr-3 flex-shrink-0"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                    <span className="text-gray-300">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                className={`w-full py-4 px-6 rounded-lg font-medium transition-all duration-300 relative overflow-hidden flex-shrink-0 ${
                  tier.isPopular
                    ? 'bg-gradient-to-r from-purple-600 to-violet-600 text-white hover:from-purple-500 hover:to-violet-500 hover:scale-105 shadow-lg hover:shadow-purple-500/25'
                    : tier.isBestValue
                    ? 'bg-gradient-to-r from-yellow-600 to-orange-600 text-white hover:from-yellow-500 hover:to-orange-500 hover:scale-105 shadow-lg hover:shadow-yellow-500/25'
                    : 'bg-gradient-to-r from-gray-700 to-gray-800 text-white hover:from-gray-600 hover:to-gray-700 hover:scale-105 border border-gray-600 shadow-lg hover:shadow-gray-500/25'
                }`}
               onClick={() => handlePay(tier, index)}
              >
                <div className="relative z-10" >
                
                    <div>
                      <div className="text-lg font-bold">{tier.buttonText}</div>
                    </div>

                </div>
                {/* 按钮光效 */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-700"></div>
              </button>
            </div>
          ))}
        </div>

        {/* 功能展示区域 */}
        <div className="mt-20">
          <div className="text-center mb-12">
            <h3 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              核心功能特性
            </h3>
            <p className="text-lg text-gray-300 max-w-3xl mx-auto">
              无论选择哪个版本，您都将获得以下核心功能支持
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {[
              {
                title: 'Web3 项目协议交互功能',
                description: '支持各种DeFi协议、NFT市场、DEX等Web3项目的自动化交互',
                icon: (
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                )
              },
              {
                title: '银河任务通用模块',
                description: '内置银河任务模板，支持快速配置和批量执行各种链上任务',
                icon: (
                  <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                )
              },
              {
                title: '指纹浏览器自动化任务',
                description: '支持多指纹浏览器环境，确保任务执行的安全性和稳定性',
                icon: (
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                )
              },
              {
                title: '多种链上工具',
                description: '提供丰富的链上分析工具、监控工具和交易工具',
                icon: (
                  <svg className="w-8 h-8 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )
              },
              {
                title: '支持多链协议任务',
                description: '兼容以太坊、BSC、Polygon、Arbitrum等主流公链',
                icon: (
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                )
              },
              {
                title: '实时技术客服支持',
                description: '7x24小时专业技术支持，确保您的使用体验',
                icon: (
                  <svg className="w-8 h-8 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192L5.636 18.364M12 2.25a9.75 9.75 0 109.75 9.75A9.75 9.75 0 0012 2.25z" />
                  </svg>
                )
              }
            ].map((feature, index) => (
              <div
                key={index}
                className="bg-gray-900/50 backdrop-blur-sm rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300 hover:scale-105 group"
              >
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0 p-2 bg-gray-800/50 rounded-lg group-hover:bg-gray-700/50 transition-colors">
                    {feature.icon}
                  </div>
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-white mb-2">
                      {feature.title}
                    </h4>
                    <p className="text-gray-400 text-sm leading-relaxed">
                      {feature.description}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 支付弹框 */}
      {showModal && selectedTier && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full mx-4 border border-gray-700 max-h-[90vh] overflow-y-auto">
            {/* 弹框头部 */}
            <div className="flex items-center justify-between p-4 border-b border-gray-700">
              <h3 className="text-lg font-bold text-white">支付信息</h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {/* 弹框内容 */}
            <div className="p-4">
              <div className="text-center mb-4">
                <div className="inline-flex items-center gap-2 bg-gradient-to-r from-red-500/20 to-orange-500/20 border border-red-500/30 rounded-lg px-3 py-1 mb-3">
                  <span className="text-red-400 font-bold text-sm"></span>
                </div>
                <h4 className="text-lg font-bold text-white mb-2">{selectedTier.name}</h4>
                <div className="text-xl font-bold bg-gradient-to-r from-green-400 to-blue-500 bg-clip-text text-transparent">
                  {selectedTier.price}
                </div>
              </div>

              {/* 支付信息 */}
              <div className="bg-gray-800/50 rounded-xl p-4">
                <div className="space-y-3">
                  {/* 收款地址 */}
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-gray-300 font-medium text-sm">收款地址</span>
                      <button 
                        onClick={() => copyToClipboard('0x34eEFA60E7aa56a58baaF63F03F6d7df025617e6', 'address')}
                        className="text-blue-400 hover:text-blue-300 transition-colors"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                        </svg>
                      </button>
                    </div>
                    <div className="font-mono text-xs text-white bg-gray-800 rounded p-2 break-all">
                      0x34eEFA60E7aa56a58baaF63F03F6d7df025617e6
                    </div>
                    {copySuccessAddress && (
                      <span className="text-green-400 text-xs mt-1 block">已复制</span>
                    )}
                  </div>

                  {/* 支付金额 */}
                  <div className="flex items-center justify-between bg-gray-700/50 rounded-lg p-3">
                    <span className="text-gray-300 text-sm">支付金额</span>
                    <span className="font-bold text-green-400">{selectedTier.price}</span>
                  </div>

                  {/* 重要提示 */}
                  <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                    <div className="flex items-start gap-2">
                      <div className="text-yellow-500 mt-0.5 text-sm">⚠️</div>
                      <div className="text-yellow-200 text-xs">
                        <p className="font-medium mb-1">重要提示</p>
                        <p>该地址为合约地址，钱包提醒转账风险为正常</p>
                        <p>仅支持 ARB/ETH/BASE/BSC 网络的 USDC/USDT 支付，请勿使用其他网络转账，否则视为无效转账</p>
                      </div>
                    </div>
                  </div>

                  {/* 客服信息 */}
                  <div className="bg-gray-700/50 rounded-lg p-3">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-6 h-6 bg-green-500 rounded-lg flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M8.691 2.188C3.891 2.188 0 5.476 0 9.53c0 2.212 1.162 4.203 2.969 5.522.098.078.223.211.324.324.099.111.176.223.25.334.123.234.211.456.25.69.027.166.014.324-.037.479-.027.08-.074.143-.125.209.235-.023.48-.111.707-.234.098-.055.19-.117.279-.186.27-.21.525-.442.755-.696 1.334-.127 2.588-.453 3.6-1.01 1.77.508 3.617.773 5.527.773 4.8 0 8.691-3.288 8.691-7.342 0-4.054-3.891-7.343-8.691-7.343zm-.362 3.591c.428 0 .775.347.775.775s-.347.775-.775.775-.775-.347-.775-.775.347-.775.775-.775zm-3.863 0c.428 0 .775.347.775.775s-.347.775-.775.775-.775-.347-.775-.775.347-.775.775-.775zm7.725 0c.428 0 .775.347.775.775s-.347.775-.775.775-.775-.347-.775-.775.347-.775.775-.775z"/>
                        </svg>
                      </div>
                      <div>
                        <p className="text-white font-medium text-sm">客服微信</p>
                      </div>
                    </div>
                                        <div className="flex items-center justify-between">
                      <span className="text-gray-300 text-xs">购买前可先添加客服微信注明来意</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-white font-bold text-sm">songsu_w</span>
                        <button 
                          onClick={() => copyToClipboard('songsu_w', 'wechat')}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                        {copySuccessWechat && (
                          <span className="text-green-400 text-xs ml-2">已复制</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default PricingSection;